import type { Vec2, WorldObstacleData } from "../types";
import { add, clip, length, scale, sub } from "./math";

export interface WorldContact {
  normal: Vec2;
  penetration: number;
  contactPoint: Vec2;
}

function toLocal(obstacle: WorldObstacleData, value: Vec2, direction = false): Vec2 {
  const cosine = Math.cos(obstacle.heading);
  const sine = Math.sin(obstacle.heading);
  const source = direction ? value : sub(value, obstacle.center);
  return [cosine * source[0] + sine * source[1], -sine * source[0] + cosine * source[1]];
}

function toWorldDirection(obstacle: WorldObstacleData, value: Vec2): Vec2 {
  const cosine = Math.cos(obstacle.heading);
  const sine = Math.sin(obstacle.heading);
  return [cosine * value[0] - sine * value[1], sine * value[0] + cosine * value[1]];
}

export function raycastObstacle(obstacle: WorldObstacleData, origin: Vec2, direction: Vec2, maxRange: number): number {
  const localOrigin = toLocal(obstacle, origin);
  const localDirection = toLocal(obstacle, direction, true);
  const half: Vec2 = [obstacle.length / 2, obstacle.width / 2];
  let near = -Infinity;
  let far = Infinity;
  for (let axis = 0; axis < 2; axis += 1) {
    const directionValue = localDirection[axis] as number;
    const originValue = localOrigin[axis] as number;
    const halfValue = half[axis] as number;
    if (Math.abs(directionValue) < 1e-12) {
      if (Math.abs(originValue) > halfValue) return maxRange;
      continue;
    }
    const first = (-halfValue - originValue) / directionValue;
    const second = (halfValue - originValue) / directionValue;
    near = Math.max(near, Math.min(first, second));
    far = Math.min(far, Math.max(first, second));
    if (near > far) return maxRange;
  }
  if (far < 0) return maxRange;
  return Math.min(maxRange, Math.max(0, near));
}

export function circleObstacleContact(obstacle: WorldObstacleData, point: Vec2, radius: number): WorldContact | null {
  const local = toLocal(obstacle, point);
  const halfX = obstacle.length / 2;
  const halfY = obstacle.width / 2;
  const closest: Vec2 = [clip(local[0], -halfX, halfX), clip(local[1], -halfY, halfY)];
  const delta = sub(local, closest);
  const distance = length(delta);
  let normalLocal: Vec2;
  let contactLocal: Vec2;
  let penetration: number;
  if (distance > 1e-12) {
    if (distance >= radius) return null;
    normalLocal = scale(delta, 1 / distance);
    contactLocal = closest;
    penetration = radius - distance;
  } else {
    const faceX = halfX - Math.abs(local[0]);
    const faceY = halfY - Math.abs(local[1]);
    if (faceX <= faceY) {
      const sign = local[0] >= 0 ? 1 : -1;
      normalLocal = [sign, 0];
      contactLocal = [sign * halfX, local[1]];
      penetration = radius + faceX;
    } else {
      const sign = local[1] >= 0 ? 1 : -1;
      normalLocal = [0, sign];
      contactLocal = [local[0], sign * halfY];
      penetration = radius + faceY;
    }
  }
  return {
    normal: toWorldDirection(obstacle, normalLocal),
    penetration,
    contactPoint: add(obstacle.center, toWorldDirection(obstacle, contactLocal)),
  };
}

export function obstacleCorners(obstacle: WorldObstacleData): [Vec2, Vec2, Vec2, Vec2] {
  const halfX = obstacle.length / 2;
  const halfY = obstacle.width / 2;
  return [
    [-halfX, -halfY], [halfX, -halfY], [halfX, halfY], [-halfX, halfY],
  ].map((local) => add(obstacle.center, toWorldDirection(obstacle, local as Vec2))) as [Vec2, Vec2, Vec2, Vec2];
}
